import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ROLES } from '../constants/roles';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { AdminUserRole, AdminUserRowDTO, HouseholdMemberRole } from '../types/Admin';

const ADMIN_ROLE_OPTIONS: AdminUserRole[] = [
  ROLES.SUPER_ADMIN,
  ROLES.PRIMARY,
  ROLES.PARTNER,
  ROLES.CHILD,
];

const HOUSEHOLD_ROLE_OPTIONS: HouseholdMemberRole[] = [ROLES.PRIMARY, ROLES.PARTNER, ROLES.CHILD];

type HouseholdRoleSelection = HouseholdMemberRole | '';

const AdminPage: React.FC = () => {
  const { loadUsers, updateUserRole, attachMember, createHouseholdForUser, removeUserFromHousehold } = useAdminUsers();

  const [users, setUsers] = useState<AdminUserRowDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [selectedRole, setSelectedRole] = useState<Record<string, AdminUserRole>>({});
  const [roleSaving, setRoleSaving] = useState<Record<string, boolean>>({});
  const [roleMessage, setRoleMessage] = useState<Record<string, string>>({});

  const [householdIdInput, setHouseholdIdInput] = useState<Record<string, string>>({});
  const [roleInHouseholdSelection, setRoleInHouseholdSelection] = useState<Record<string, HouseholdRoleSelection>>({});
  const [householdSaving, setHouseholdSaving] = useState<Record<string, boolean>>({});
  const [householdMessage, setHouseholdMessage] = useState<Record<string, string>>({});

  const refreshUsers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const rows = await loadUsers();
      setUsers(rows);

      setSelectedRole((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          next[row.id] = prev[row.id] ?? row.role;
        });
        return next;
      });

      setRoleInHouseholdSelection((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          next[row.id] = prev[row.id] ?? '';
        });
        return next;
      });

      setHouseholdIdInput((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          next[row.id] = row.householdId ?? prev[row.id] ?? '';
        });
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load admin users.');
    } finally {
      setLoading(false);
    }
  }, [loadUsers]);

  useEffect(() => {
    void refreshUsers();
  }, [refreshUsers]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;

    return users.filter((user) => {
      const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim().toLowerCase();
      return fullName.includes(term) || user.email.toLowerCase().includes(term);
    });
  }, [search, users]);

  const formatDisplayName = (user: AdminUserRowDTO): string => {
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || user.email;
  };

  const handleRoleSave = async (userId: string) => {
    const nextRole = selectedRole[userId];
    if (!nextRole) return;

    setRoleSaving((prev) => ({ ...prev, [userId]: true }));
    setRoleMessage((prev) => ({ ...prev, [userId]: '' }));

    try {
      await updateUserRole(userId, nextRole);
      setRoleMessage((prev) => ({ ...prev, [userId]: 'Saved' }));
      await refreshUsers();
    } catch (err) {
      setRoleMessage((prev) => ({ ...prev, [userId]: err instanceof Error ? err.message : 'Unable to save role.' }));
    } finally {
      setRoleSaving((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleAssign = async (userId: string) => {
    const existingHouseholdId = users.find((candidate) => candidate.id === userId)?.householdId ?? null;
    if (existingHouseholdId) {
      setHouseholdMessage((prev) => ({ ...prev, [userId]: 'Already assigned' }));
      return;
    }

    const householdId = (householdIdInput[userId] ?? '').trim();
    if (!householdId) {
      setHouseholdMessage((prev) => ({ ...prev, [userId]: 'Household ID is required.' }));
      return;
    }

    const roleSelection = roleInHouseholdSelection[userId] ?? '';

    setHouseholdSaving((prev) => ({ ...prev, [userId]: true }));
    setHouseholdMessage((prev) => ({ ...prev, [userId]: '' }));

    try {
      await attachMember(
        householdId,
        userId,
        roleSelection === '' ? undefined : roleSelection
      );
      setHouseholdMessage((prev) => ({ ...prev, [userId]: 'Assigned' }));
      setUsers((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, householdId } : user))
      );
    } catch (err) {
      setHouseholdMessage((prev) => ({
        ...prev,
        [userId]: err instanceof Error ? err.message : 'Unable to assign household member.',
      }));
    } finally {
      setHouseholdSaving((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleCreateHousehold = async (user: AdminUserRowDTO) => {
    if (user.householdId) {
      setHouseholdMessage((prev) => ({ ...prev, [user.id]: 'Already assigned' }));
      return;
    }

    setHouseholdSaving((prev) => ({ ...prev, [user.id]: true }));
    setHouseholdMessage((prev) => ({ ...prev, [user.id]: '' }));

    try {
      const firstName = (user.firstName ?? '').trim();
      const suggestedName = firstName ? `${firstName} Household` : undefined;
      const result = await createHouseholdForUser(user.id, suggestedName);

      setHouseholdIdInput((prev) => ({
        ...prev,
        [user.id]: result.household.id,
      }));
      setUsers((prev) =>
        prev.map((candidate) =>
          candidate.id === user.id ? { ...candidate, householdId: result.household.id } : candidate
        )
      );
      setHouseholdMessage((prev) => ({ ...prev, [user.id]: 'Household ready' }));
    } catch (err) {
      setHouseholdMessage((prev) => ({
        ...prev,
        [user.id]: err instanceof Error ? err.message : 'Unable to create household.',
      }));
    } finally {
      setHouseholdSaving((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const handleRemoveHousehold = async (userId: string) => {
    setHouseholdSaving((prev) => ({ ...prev, [userId]: true }));
    setHouseholdMessage((prev) => ({ ...prev, [userId]: '' }));

    try {
      await removeUserFromHousehold(userId);
      setHouseholdMessage((prev) => ({ ...prev, [userId]: 'Removed' }));
      await refreshUsers();
    } catch (err) {
      setHouseholdMessage((prev) => ({
        ...prev,
        [userId]: err instanceof Error ? err.message : 'Unable to remove household member.',
      }));
    } finally {
      setHouseholdSaving((prev) => ({ ...prev, [userId]: false }));
    }
  };

  if (loading && users.length === 0) {
    return <Typography>Loading admin users...</Typography>;
  }

  if (error && users.length === 0) {
    return (
      <Box>
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="outlined" onClick={() => void refreshUsers()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        Admin
      </Typography>

      <TextField
        label="Search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search by name or email"
        sx={{ mb: 2, maxWidth: 420 }}
        fullWidth
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{formatDisplayName(user)}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'grid', gap: 1, minWidth: 340 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Select
                        native
                        size="small"
                        value={selectedRole[user.id] ?? user.role}
                        onChange={(event) =>
                          setSelectedRole((prev) => ({
                            ...prev,
                            [user.id]: event.target.value as AdminUserRole,
                          }))
                        }
                        inputProps={{ 'data-testid': `role-select-${user.id}` }}
                      >
                        {ADMIN_ROLE_OPTIONS.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </Select>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={Boolean(roleSaving[user.id])}
                        onClick={() => void handleRoleSave(user.id)}
                      >
                        Save
                      </Button>
                    </Box>
                    {roleMessage[user.id] ? (
                      <Typography
                        variant="body2"
                        color={roleMessage[user.id] === 'Saved' ? 'success.main' : 'error'}
                        data-testid={`role-message-${user.id}`}
                      >
                        {roleMessage[user.id]}
                      </Typography>
                    ) : null}

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {user.householdId ? (
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          disabled={Boolean(householdSaving[user.id])}
                          onClick={() => void handleRemoveHousehold(user.id)}
                        >
                          Remove
                        </Button>
                      ) : null}
                      {user.householdId ? (
                        <Button variant="outlined" size="small" disabled>
                          Create household
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={Boolean(householdSaving[user.id])}
                          onClick={() => void handleCreateHousehold(user)}
                        >
                          Create household
                        </Button>
                      )}
                      <TextField
                        size="small"
                        label="Household ID"
                        value={householdIdInput[user.id] ?? ''}
                        disabled={Boolean(user.householdId)}
                        onChange={(event) =>
                          setHouseholdIdInput((prev) => ({
                            ...prev,
                            [user.id]: event.target.value,
                          }))
                        }
                        inputProps={{ 'data-testid': `household-id-input-${user.id}` }}
                      />
                      <Select
                        native
                        size="small"
                        value={roleInHouseholdSelection[user.id] ?? ''}
                        disabled={Boolean(user.householdId)}
                        onChange={(event) =>
                          setRoleInHouseholdSelection((prev) => ({
                            ...prev,
                            [user.id]: event.target.value as HouseholdRoleSelection,
                          }))
                        }
                        inputProps={{ 'data-testid': `household-role-select-${user.id}` }}
                      >
                        <option value="">(derive)</option>
                        {HOUSEHOLD_ROLE_OPTIONS.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </Select>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={Boolean(householdSaving[user.id]) || Boolean(user.householdId)}
                        onClick={() => void handleAssign(user.id)}
                      >
                        Assign
                      </Button>
                    </Box>
                    {user.householdId && !householdMessage[user.id] ? (
                      <Typography variant="body2" color="text.secondary" data-testid={`household-assigned-${user.id}`}>
                        Already assigned
                      </Typography>
                    ) : null}
                    {householdMessage[user.id] ? (
                      <Typography
                        variant="body2"
                        color={
                          householdMessage[user.id] === 'Assigned' ||
                          householdMessage[user.id] === 'Household ready' ||
                          householdMessage[user.id] === 'Already assigned' ||
                          householdMessage[user.id] === 'Removed'
                            ? 'success.main'
                            : 'error'
                        }
                        data-testid={`household-message-${user.id}`}
                      >
                        {householdMessage[user.id]}
                      </Typography>
                    ) : null}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AdminPage;
